/**
 * A run Clu started on a list (a skill such as Employment check, an AI
 * column, an enrichment) as one row under its answer in the agents look, the
 * size of a step row: what it is doing and how far it got while it runs,
 * what it did once it ends, and a link to the list. One clock: the run's own
 * start, in whole seconds, as the steps line counts.
 */
import { formatStepsDuration } from '@/components/agent-chat/steps.util';
import type { AgentFilterCondition } from '@/store/features/ui.feature';

/** The displays a started run arrives as. */
export const SKILL_RUN_DISPLAY = 'smart_column_started';
export const ENRICHMENT_RUN_DISPLAY = 'enrichment_started';

/**
 * Where a run is. `loading`: reloaded from history, its task not asked yet.
 * `unknown`: its task can't be read right now (a 503, a network blip), so
 * the row claims neither progress nor an end.
 */
export type RunState =
  | 'loading'
  | 'unknown'
  | 'running'
  | 'completed'
  | 'failed';

/** Where a run is: rows done (found or not) of the rows it takes. */
export interface RunProgress {
  state: RunState;
  done: number;
  total: number;
  failed: number;
  /** Epoch ms; null when nothing says when it started. */
  startedAt: number | null;
  completedAt: number | null;
  /** An enrichment's finds. */
  emails?: number;
  phones?: number;
}

/** What ran: a skill or column by its type and name, or an enrichment. */
export interface RunSubject {
  kind: 'skill' | 'enrichment';
  /** A skill's column type (`employment_check`, `research`, ...). */
  type?: string;
  name: string;
}

export function textOf(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

export function countOf(value: unknown): number {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0
    ? Math.floor(n)
    : 0;
}

/** A skill run's subject from its started result. */
export function skillSubjectOf(result: Record<string, unknown>): RunSubject {
  const type = textOf(result.column_type);
  return {
    kind: 'skill',
    type,
    name: textOf(result.column_label) ?? type ?? 'Skill',
  };
}

export const ENRICHMENT_SUBJECT: RunSubject = {
  kind: 'enrichment',
  name: 'Enrichment',
};

function counted(n: number, one: string, many: string): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

const people = (n: number) => counted(n, 'person', 'people');

/** The skills and the enrichment that read as a verb on people. */
const PEOPLE_VERBS: Record<string, { running: string; done: string }> = {
  employment_check: { running: 'Checking', done: 'Checked' },
  research: { running: 'Researching', done: 'Researched' },
  qualification: { running: 'Scoring', done: 'Scored' },
  enrichment: { running: 'Enriching', done: 'Enriched' },
};

function verbsOf(subject: RunSubject) {
  return PEOPLE_VERBS[
    subject.kind === 'enrichment' ? 'enrichment' : (subject.type ?? '')
  ];
}

/** "Checking 10 people", "Running Seniority on 50 people". */
export function runningLabel(subject: RunSubject, total: number): string {
  const verbs = verbsOf(subject);
  return verbs
    ? `${verbs.running} ${people(total)}`
    : `Running ${subject.name} on ${people(total)}`;
}

/** A run whose state isn't known: "Employment check on 10 people". */
export function neutralLabel(subject: RunSubject, total: number): string {
  return `${subject.name} on ${people(total)}`;
}

/** "Checked 10 people", or "Checked 9 of 10 people" when some failed. */
export function doneLabel(
  subject: RunSubject,
  total: number,
  succeeded: number
): string {
  const verbs = verbsOf(subject);
  const partial = succeeded < total;
  if (verbs) {
    return partial
      ? `${verbs.done} ${succeeded.toLocaleString()} of ${people(total)}`
      : `${verbs.done} ${people(total)}`;
  }
  return partial
    ? `Ran ${subject.name} on ${succeeded.toLocaleString()} of ${people(total)}`
    : `Ran ${subject.name} on ${people(total)}`;
}

/** An enrichment's finds: "18 emails, 9 phones". */
function findsOf(progress: RunProgress): string {
  const parts: string[] = [];
  if (progress.emails !== undefined) {
    parts.push(counted(progress.emails, 'email', 'emails'));
  }
  if (progress.phones !== undefined) {
    parts.push(counted(progress.phones, 'phone', 'phones'));
  }
  return parts.join(', ');
}

/** `neutral`: neither running nor ended, as far as the row can tell. */
export type RunTone = 'running' | 'neutral' | 'done' | 'failed';

/** What the row shows. */
export interface RunRowView {
  tone: RunTone;
  label: string;
  /** Muted caption parts, joined by " · ". */
  caption: string[];
  /** "1 failed", in amber after the caption. */
  failed?: string;
}

function elapsed(progress: RunProgress, now: number): string | null {
  if (progress.startedAt === null) return null;
  if (progress.state === 'loading' || progress.state === 'unknown') return null;
  const end = progress.state === 'running' ? now : progress.completedAt;
  return end === null ? null : formatStepsDuration(end - progress.startedAt);
}

export function runRowView(
  subject: RunSubject,
  progress: RunProgress,
  now: number
): RunRowView {
  const total = Math.max(progress.total, progress.done);
  const time = elapsed(progress, now);
  switch (progress.state) {
    // Reloaded and not yet answered, or its task can't be read: the run by
    // name, no count, no clock, no end.
    case 'loading':
      return {
        tone: 'running',
        label: neutralLabel(subject, total),
        caption: [],
      };
    case 'unknown':
      return {
        tone: 'neutral',
        label: neutralLabel(subject, total),
        caption: [],
      };
    case 'running':
      return {
        tone: 'running',
        label: runningLabel(subject, total),
        caption: [
          `${Math.min(progress.done, total).toLocaleString()} of ${total.toLocaleString()}`,
          ...(time ? [time] : []),
        ],
      };
    case 'failed':
      return {
        tone: 'failed',
        label: `${subject.name} failed`,
        caption: [
          `${Math.min(progress.done, total).toLocaleString()} of ${total.toLocaleString()}`,
          ...(time ? [time] : []),
        ],
      };
    case 'completed': {
      const failed = Math.min(progress.failed, total);
      const label = doneLabel(subject, total, total - failed);
      const finds = subject.kind === 'enrichment' ? findsOf(progress) : '';
      return {
        tone: 'done',
        label: finds ? `${label}: ${finds}` : label,
        caption: time ? [time] : [],
        ...(failed > 0 ? { failed: `${failed.toLocaleString()} failed` } : {}),
      };
    }
  }
}

/**
 * The rows a finished skill wrote, as the list's own filter: its result
 * column is not empty. Only Employment check names its column this way
 * (`smart_employment_check`); any other run opens the list as it is.
 */
export function resultRowsFilter(
  subject: RunSubject
): AgentFilterCondition[] | undefined {
  if (subject.kind !== 'skill' || subject.type !== 'employment_check') {
    return undefined;
  }
  return [
    {
      id: '1',
      field: `smart_${subject.type}`,
      operator: 'is_not_empty',
      value: '',
    },
  ];
}
